import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import {
  Dialog as AriaDialog,
  Heading,
  Modal,
  ModalOverlay,
  Popover,
  type PopoverProps,
} from 'react-aria-components';
import { Button } from './Button.tsx';
import styles from './Dialog.module.css';

interface ModalDialogProps {
  title: string;
  children: ReactNode | ((close: () => void) => ReactNode);
  /** Footer actions; receives close. */
  actions?: (close: () => void) => ReactNode;
  wide?: boolean;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

// Modal dialog: contains focus, closes with Escape, returns focus to its trigger
// (DESIGN_SYSTEM.md §16). Use inside a react-aria DialogTrigger, or control it.
export function ModalDialog({
  title,
  children,
  actions,
  wide = false,
  isOpen,
  onOpenChange,
}: ModalDialogProps) {
  return (
    <ModalOverlay
      className={styles.overlay}
      isDismissable
      {...(isOpen === undefined ? {} : { isOpen })}
      {...(onOpenChange ? { onOpenChange } : {})}
    >
      <Modal className={[styles.modal, wide ? styles.wide : ''].join(' ')}>
        <AriaDialog className={styles.dialog}>
          {({ close }) => (
            <>
              <header className={styles.header}>
                <Heading slot="title" className={styles.title}>
                  {title}
                </Heading>
                <Button variant="secondary" size="small" onPress={close} aria-label="Close">
                  <X size={18} aria-hidden="true" />
                </Button>
              </header>
              <div className={styles.body}>
                {typeof children === 'function' ? children(close) : children}
              </div>
              {actions && <footer className={styles.footer}>{actions(close)}</footer>}
            </>
          )}
        </AriaDialog>
      </Modal>
    </ModalOverlay>
  );
}

interface PopoverDialogProps {
  title: string;
  children: ReactNode;
  placement?: PopoverProps['placement'];
}

/** Small non-modal explanation popover with focus containment and Escape. */
export function PopoverDialog({ title, children, placement = 'bottom end' }: PopoverDialogProps) {
  return (
    <Popover className={styles.popover} placement={placement} offset={8}>
      <AriaDialog className={styles.popoverDialog}>
        {({ close }) => (
          <>
            <Heading slot="title" className={styles.popoverTitle}>
              {title}
            </Heading>
            <div className={styles.popoverBody}>{children}</div>
            <Button size="small" onPress={close}>
              Got it
            </Button>
          </>
        )}
      </AriaDialog>
    </Popover>
  );
}
